/**
 * relationshipDisplayUtils - Utilities for extracting verb and display names
 * from TWG V2 relationship schemas.
 *
 * Verb extraction uses the V2 `fromTypeName`/`targetTypeName` fields (PascalCase
 * node type names) converted to kebab-case, with aliases for known divergences.
 *
 * Ported from analyze-relationships.js (the V2 CLI analysis script).
 */

import type { Relationship } from '../../../../labs/home/hooks/useRelationships';
import { type RelationshipInfo } from '../services/cypherQueryService';
// ============================================================================
// Cypher ↔ hyphen conversion
// ============================================================================

export function cypherToHyphen(type: string): string {
	return type.replace(/_/g, '-');
}

function hyphenToCypher(name: string): string {
	return name.replace(/-/g, '_');
}

// ============================================================================
// PascalCase → kebab-case conversion
// ============================================================================

/**
 * Converts a PascalCase type name to kebab-case.
 * e.g. "AtlassianUser" → "atlassian-user"
 *      "JiraWorkItem" → "jira-work-item"
 *      "Customer360Customer" → "customer360-customer"
 */
function toKebab(str: string): string {
	return str
		.replace(/(\d)([A-Z])/g, '$1-$2')
		.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
		.replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
		.toLowerCase();
}

// ============================================================================
// Noun aliases (V2): maps nouns used in relationship names to their
// kebab-cased fromTypeName/targetTypeName equivalents
// ============================================================================

const NOUN_ALIASES: Record<string, string[]> = {
	// identity
	'external-user': ['atlassian-user', 'third-party-user'],

	// JSM semantic aliases
	'jsm-incident': ['jira-work-item', 'external-incident'],
	'jsm-space': ['jira-space'],
	'jsm-post-incident-review': ['jira-work-item'],
	'jsm-post-incident-review-link': [
		'jira-post-incident-review-link',
		'external-post-incident-review',
	],
	'jira-post-incident-review': ['jira-work-item'],

	// Composite entity nouns (multiple types collapsed into one name-noun)
	'content-entity': ['jira-work-item', 'confluence-page', 'confluence-blogpost'],
	'work-entity': ['atlassian-project', 'jira-work-item', 'jira-align-project'],
	'space-entity': ['jira-space', 'confluence-space', 'loom-space'],
	'document-entity': [
		'confluence-space',
		'confluence-page',
		'confluence-folder',
		'confluence-whiteboard',
		'external-document',
	],
	'incident-entity': ['jira-work-item', 'external-incident'],
	'investment-entity': ['focus-focus-area', 'jira-align-project'],
	'support-escalation-entity': ['jira-space', 'jira-work-item'],
	'knowledge-base-entity': ['external-document', 'confluence-space'],
	entity: ['confluence-page', 'confluence-blogpost'],
	'related-entity': ['confluence-page', 'confluence-blogpost', 'atlassian-user'],
	'jira-for-you-recommendation-entity': [
		'jira-work-item',
		'jira-space',
		'jira-work-item-comment',
		'atlassian-team',
		'external-pull-request',
	],
	'repository-entity': ['external-repository'],

	// Loom aliases
	'loom-video-comment': ['loom-comment'],
	'external-meeting-recurrence': ['loom-meeting-recurrence'],

	// Confluence decorated nouns
	'confluence-meeting-notes-folder': ['confluence-folder'],
	'confluence-meeting-notes-page': ['confluence-page'],
	'confluence-content': ['confluence-space'],

	// Jira release/version
	'jira-release': ['jira-version'],
	'jira-release-version': ['jira-version'],

	// Jira semantic aliases
	'jira-epic': ['jira-work-item'],
	'jira-priority': ['jira-work-item-priority'],
	'jira-status': ['jira-work-item-status'],
	'jira-app-installation': ['jira-connect-app'],
	'customer-support-project': ['jira-space'],
	'support-escalation-project': ['jira-space'],

	// Organisation shorthand
	'external-org': ['external-organisation'],

	// Customer 360
	'customer-360-customer': ['customer360-customer'],

	// Assets
	'asset-type': ['assets-type'],

	// Topic
	topic: ['knowledge-discovery-topic'],

	// External position
	'external-position': ['talent-position'],

	// External calendar shorthand
	'external-calendar': ['external-calendar-event'],

	// Agent
	'atlassian-agent': ['atlassian-user'],

	// Generic entity catch-alls (many-to-many type mappings)
	'external-entity': [
		'external-document',
		'external-remote-link',
		'external-video',
		'external-message',
		'external-conversation',
		'external-branch',
		'external-build',
		'external-commit',
		'external-deployment',
		'external-repository',
		'external-vulnerability',
	],
	'third-party-entity': [
		'third-party-document',
		'third-party-remote-link',
		'third-party-video',
		'third-party-message',
		'third-party-conversation',
		'third-party-branch',
		'third-party-build',
		'third-party-commit',
		'third-party-deployment',
		'third-party-pull-request',
		'third-party-repository',
		'third-party-vulnerability',
	],

	// Name uses "external-branch" but targetTypeName is ExternalRepository
	'external-branch': ['external-repository'],
};

// ============================================================================
// Manual verb overrides for non-standard relationship names
// ============================================================================

const MANUAL_VERB_MAP: Record<string, { fromNoun: string; verb: string; toNoun: string }> = {
	// graph-to-3p replication
	'graph-document-3p-document': {
		fromNoun: 'document',
		verb: 'replicates',
		toNoun: 'third-party-document',
	},
};

// ============================================================================
// Core decomposition logic (V2 — PascalCase-to-kebab approach)
// ============================================================================

/**
 * Builds the full set of noun candidates for a given typeName array.
 * Includes direct kebab conversions and alias matches.
 */
function getCandidates(typeNameArray: string[]): string[] {
	const candidates: string[] = [];
	const seen = new Set<string>();

	function add(noun: string) {
		if (!seen.has(noun)) {
			seen.add(noun);
			candidates.push(noun);
		}
	}

	// Direct kebab conversions
	for (const tn of typeNameArray) {
		add(toKebab(tn));
	}

	// Aliases: if any kebab typeName matches the alias's target list, add the alias
	const kebabNames = typeNameArray.map(toKebab);
	for (const [alias, targets] of Object.entries(NOUN_ALIASES)) {
		if (targets.some((t) => kebabNames.includes(t))) {
			add(alias);
		}
	}

	return candidates;
}

function stripPrefix(name: string, noun: string): string | null {
	const prefix = noun + '-';
	if (name.startsWith(prefix)) {
		return name.slice(prefix.length);
	}
	return null;
}

function stripSuffix(name: string, noun: string): string | null {
	const suffix = '-' + noun;
	if (name.endsWith(suffix)) {
		return name.slice(0, name.length - suffix.length);
	}
	return null;
}

export interface DecomposedRelationship {
	fromNoun: string;
	verb: string;
	toNoun: string;
}

/**
 * Decomposes a relationship name into { fromNoun, verb, toNoun }.
 *
 * Uses the V2 `fromTypeName`/`targetTypeName` fields (PascalCase names)
 * converted to kebab-case for noun matching.
 *
 * @param name - Hyphenated relationship name (e.g. "user-viewed-page")
 * @param fromTypeNames - PascalCase type names for the source node types
 * @param targetTypeNames - PascalCase type names for the target node types
 * @returns The decomposition, or null if no match found
 */
function decomposeRelationshipName(
	name: string,
	fromTypeNames: string[],
	targetTypeNames: string[],
): DecomposedRelationship | null {
	if (MANUAL_VERB_MAP[name]) {
		return MANUAL_VERB_MAP[name];
	}

	const fromCandidates = getCandidates(fromTypeNames);
	const toCandidates = getCandidates(targetTypeNames);

	// Sort by length descending to prefer more specific (longer) noun matches
	fromCandidates.sort((a, b) => b.length - a.length);
	toCandidates.sort((a, b) => b.length - a.length);

	const matches: Array<DecomposedRelationship & { score: number }> = [];

	for (const fromNoun of fromCandidates) {
		const afterFrom = stripPrefix(name, fromNoun);
		if (afterFrom === null) {
			continue;
		}

		for (const toNoun of toCandidates) {
			const verb = stripSuffix(afterFrom, toNoun);
			if (verb === null) {
				continue;
			}

			if (verb.length > 0 && !verb.startsWith('-') && !verb.endsWith('-')) {
				matches.push({
					fromNoun,
					verb,
					toNoun,
					score: fromNoun.length + toNoun.length,
				});
			}
		}
	}

	if (matches.length > 0) {
		matches.sort((a, b) => b.score - a.score);
		const best = matches[0];
		return { fromNoun: best.fromNoun, verb: best.verb, toNoun: best.toNoun };
	}

	return null;
}

/**
 * Extracts just the verb from a relationship type name. Falls back to the
 * full hyphenated name when decomposition isn't possible.
 *
 * @param cypherRelType - Relationship type in Cypher underscore format (e.g. "user_viewed_page")
 * @param allRelationships - Full relationship schema from TWG
 */
export function extractRelationshipVerb(
	cypherRelType: string,
	allRelationships: Record<string, Relationship>,
): string {
	const hyphenName = cypherToHyphen(cypherRelType);
	const rel = findRelationshipByType(cypherRelType, allRelationships);

	if (rel) {
		const fromTypeNames = rel.fromTypeName ?? [];
		const targetTypeNames = rel.targetTypeName ?? [];
		const result = decomposeRelationshipName(hyphenName, fromTypeNames, targetTypeNames);
		if (result) {
			return result.verb;
		}
	}

	// Heuristic fallback when the rel isn't in the schema (or decomposition
	// failed). Names tend to follow `<from-noun>-<verb>-<to-noun>` patterns
	// (e.g. `content-entity-linked-entity` → "linked"). Strip the known
	// generic boundary nouns from both ends, then return whatever's left.
	return guessVerbFromHyphenName(hyphenName);
}

/**
 * Generic last-resort verb extractor for hyphenated relationship names that
 * the schema doesn't cover. Trims well-known noun fragments off the front
 * and back; if the result is a single word, returns it; otherwise falls back
 * to the original hyphen-joined name so callers always have *something* to
 * display.
 */
const NOUN_FRAGMENTS = new Set([
	'atlassian',
	'user',
	'users',
	'account',
	'accounts',
	'identity',
	'identities',
	'content',
	'contents',
	'entity',
	'entities',
	'object',
	'objects',
	'item',
	'items',
	'work',
	'item',
	'thing',
	'things',
	'page',
	'pages',
	'jira',
	'confluence',
	'loom',
	'video',
	'videos',
	'ws',
	'workspace',
	'workspaces',
	'comment',
	'comments',
	'attachment',
	'attachments',
	'project',
	'projects',
	'issue',
	'issues',
	'space',
	'spaces',
	'team',
	'teams',
	'folder',
	'folders',
	'database',
	'databases',
]);

function guessVerbFromHyphenName(hyphenName: string): string {
	const parts = hyphenName.split('-').filter(Boolean);
	let start = 0;
	let end = parts.length;
	while (start < end && NOUN_FRAGMENTS.has(parts[start].toLowerCase())) {
		start += 1;
	}
	while (end > start && NOUN_FRAGMENTS.has(parts[end - 1].toLowerCase())) {
		end -= 1;
	}
	const middle = parts.slice(start, end);
	if (middle.length === 0) {
		return hyphenName;
	}
	return middle.join('-');
}

// ============================================================================
// Node display name helpers
// ============================================================================

/**
 * Gets the display name for the "other end" of a relationship.
 *
 * For incoming relationships, the other end is the source (fromTypeName).
 * For outgoing relationships, the other end is the target (targetTypeName).
 *
 * @param cypherRelType - Relationship type in Cypher underscore format
 * @param direction - 'incoming' or 'outgoing'
 * @param allRelationships - Full relationship schema from TWG
 * @param typenameDisplayMap - Map from node type name to display name
 */
export function getOtherEndDisplayName(
	cypherRelType: string,
	direction: 'incoming' | 'outgoing',
	allRelationships: Record<string, Relationship>,
	typenameDisplayMap: Record<string, string>,
): string {
	const rel = findRelationshipByType(cypherRelType, allRelationships);
	if (!rel) {
		return cypherToHyphen(cypherRelType);
	}

	const typeNames = direction === 'incoming' ? rel.fromTypeName : rel.targetTypeName;
	if (!typeNames || typeNames.length === 0) {
		return cypherToHyphen(cypherRelType);
	}

	// Try to resolve the first type name to a display name
	const firstName = typeNames[0];
	if (typenameDisplayMap[firstName]) {
		return typenameDisplayMap[firstName];
	}

	// Fallback: insert spaces before uppercase letters for PascalCase names
	return firstName.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Finds a Relationship entry by Cypher-format type name.
 * Relationship schema keys are hyphenated, so we convert.
 */
function findRelationshipByType(
	cypherRelType: string,
	allRelationships: Record<string, Relationship>,
): Relationship | null {
	const hyphenName = cypherToHyphen(cypherRelType);
	if (allRelationships[hyphenName]) {
		return allRelationships[hyphenName];
	}

	// Also try matching by converting each schema name to Cypher format
	for (const [key, rel] of Object.entries(allRelationships)) {
		if (hyphenToCypher(key) === cypherRelType) {
			return rel;
		}
	}

	return null;
}

// ============================================================================
// Relationship grouping for the exploration panel
// ============================================================================

export interface RelationshipGroup<T extends RelationshipInfo = RelationshipInfo> {
	/** Display name for the group (e.g. node type name like "Confluence Page", or product like "Jira"). */
	name: string;
	/** The relationships belonging to this group, sorted by count descending. */
	relationships: T[];
}

/**
 * Extracts the product name from an ATI-format type string.
 *
 * ATI format: `ati:cloud:<product>:<entityType>`
 * Example: `ati:cloud:confluence:page` → `"confluence"`
// ============================================================================
// ATI-based product extraction (ported from home)
// ============================================================================

/**
 * Extracts the product segment from an ATI type string.
 * e.g. `ati:cloud:jira:document` → `jira`
 * Returns null if the format doesn't match.
 */
function extractProductFromAtiType(atiType: string): string | null {
	const parts = atiType.split(':');
	if (parts.length >= 4) {
		return parts[2];
	}
	return null;
}

/**
 * Extracts the most representative product from an array of ATI type strings.
 *
 * External/third-party entities in the TWG schema are typically registered
 * under both `jira` and `graph` products (e.g. `ati:cloud:jira:document` +
 * `ati:cloud:graph:document`). These are not truly Jira entities, so we
 * classify them as "external" to avoid misleading grouping.
 */
export function extractBestProductFromAtiTypes(atiTypes: string[]): string | null {
	if (atiTypes.length === 0) {
		return null;
	}

	// Third-party ATIs (e.g. ati:third-party:google.google-drive:document)
	if (atiTypes.some((t) => t.startsWith('ati:third-party:'))) {
		return 'third-party';
	}

	const products = atiTypes.map(extractProductFromAtiType).filter((p): p is string => p !== null);

	if (products.length === 0) {
		return null;
	}

	// When both jira and graph are present, the entity is an external type
	// registered across products (e.g. ExternalDocument, ExternalCommit).
	if (products.includes('jira') && products.includes('graph')) {
		return 'external';
	}

	// Prefer a specific product over jira or graph when available.
	return products.find((p) => p !== 'jira' && p !== 'graph') ?? products[0];
}
